import { useEffect, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import Barcode from 'react-barcode';
import html2canvas from 'html2canvas';
import { ImageEncoder } from '@mmote/niimbluelib';
import { Button } from '@/components/ui/button';
import { Bluetooth, BluetoothOff, Loader2, Unplug } from 'lucide-react';
import { toast } from 'sonner';
import { Shipment } from '@/types/shipment.types';
import {
  disconnectNiimbotClient,
  getCurrentNiimbotClient,
  getNiimbotClient,
} from '@/lib/niimbot-client';

interface NiimbotB1PrintButtonProps {
  shipment: Shipment;
  quantity?: number;
  density?: number;
  /** Show a visible preview thumbnail of the label that will be printed */
  showPreview?: boolean;
}

// Niimbot B1: printhead is 384 dots wide (~48mm @ 203 DPI), label feed = 8 dots/mm.
// For a 50×30mm label, max printable area is 384 × 240 px.
const B1_WIDTH = 384;
const B1_HEIGHT = 240;

export function NiimbotB1PrintButton({
  shipment,
  quantity = 1,
  density = 3,
  showPreview = true,
}: NiimbotB1PrintButtonProps) {
  const labelRef = useRef<HTMLDivElement>(null);
  const [printing, setPrinting] = useState(false);
  const [connected, setConnected] = useState<boolean>(
    () => !!getCurrentNiimbotClient()
  );

  // Re-check on mount in case another button on the page already connected
  useEffect(() => {
    setConnected(!!getCurrentNiimbotClient());
  }, []);

  const handlePrint = async () => {
    if (!labelRef.current) return;
    setPrinting(true);

    try {
      const sourceCanvas = await html2canvas(labelRef.current, {
        backgroundColor: '#ffffff',
        scale: 1,
      });

      const canvas = document.createElement('canvas');
      canvas.width = B1_WIDTH;
      canvas.height = B1_HEIGHT;
      const ctx = canvas.getContext('2d')!;
      ctx.imageSmoothingEnabled = false;
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(sourceCanvas, 0, 0, canvas.width, canvas.height);

      const { client, mode, deviceName } = await getNiimbotClient();
      setConnected(true);
      if (mode === 'silent-reconnect') {
        toast.info(`متصل بـ ${deviceName ?? 'Niimbot'} بدون اختيار`);
      }

      const encoded = ImageEncoder.encodeCanvas(canvas, 'top');
      const printTaskName = client.getPrintTaskType() ?? 'B1';
      const printTask = client.abstraction.newPrintTask(printTaskName, {
        totalPages: quantity,
        density,
        statusPollIntervalMs: 100,
        statusTimeoutMs: 8_000,
      });

      await printTask.printInit();
      await printTask.printPage(encoded, quantity);
      await printTask.waitForPageFinished();
      await printTask.waitForFinished();
      await printTask.printEnd();

      toast.success('تمت الطباعة بنجاح');
    } catch (err) {
      const isCancel =
        err instanceof DOMException && err.name === 'NotFoundError';
      if (isCancel) {
        console.info('[NiimbotB1] device chooser cancelled');
      } else {
        console.error('[NiimbotB1] print failed', err);
        toast.error(`فشل الطباعة: ${err instanceof Error ? err.message : String(err)}`);
      }
      // Refresh connected state in case the connection dropped during print
      setConnected(!!getCurrentNiimbotClient());
    } finally {
      setPrinting(false);
    }
  };

  const handleDisconnect = async () => {
    await disconnectNiimbotClient();
    setConnected(false);
    toast.info('تم فصل الطابعة');
  };

  return (
    <>
      {/* Off-screen B1 label rendered at exact printer-pixel dimensions (used for rasterization) */}
      <div
        style={{
          position: 'fixed',
          left: -99999,
          top: 0,
          pointerEvents: 'none',
          opacity: 0,
        }}
        aria-hidden
        dir="ltr"
      >
        <div ref={labelRef}>
          <NiimbotB1Label shipment={shipment} />
        </div>
      </div>

      <div className="flex items-center gap-2">
        {showPreview && (
          <div
            className="rounded border border-dashed border-muted-foreground/40 bg-white shrink-0 overflow-hidden"
            title="معاينة الملصق Niimbot B1"
          >
            {/* `zoom` shrinks layout box (not just visual), so parent sizes to ~230×144 */}
            <div style={{ zoom: 0.6 }} dir="ltr">
              <NiimbotB1Label shipment={shipment} />
            </div>
          </div>
        )}

        <div className="flex flex-col items-stretch gap-1">
          <Button
            onClick={handlePrint}
            disabled={printing}
            variant="secondary"
            className="gap-2"
          >
            {printing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Bluetooth className={`h-4 w-4 ${connected ? 'text-blue-600' : ''}`} />
            )}
            طباعة Niimbot B1
          </Button>
          {connected && (
            <button
              type="button"
              onClick={handleDisconnect}
              disabled={printing}
              className="flex items-center justify-center gap-1 text-xs text-muted-foreground hover:text-destructive disabled:opacity-50"
            >
              <Unplug className="h-3 w-3" />
              فصل
              <BluetoothOff className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
    </>
  );
}

function NiimbotB1Label({ shipment }: { shipment: Shipment }) {
  const tracking = shipment.tracking_number;

  return (
    <div
      style={{
        width: B1_WIDTH,
        height: B1_HEIGHT,
        backgroundColor: '#fff',
        color: '#000',
        fontFamily: 'Arial, sans-serif',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 6,
      }}
    >
      {/* Top row: QR + tracking number */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', width: '100%' }}>
        <QRCodeSVG value={tracking} size={120} />
        <div
          style={{
            flex: 1,
            fontSize: 30,
            fontWeight: 900,
            letterSpacing: 1,
            lineHeight: 1.05,
            textAlign: 'center',
            wordBreak: 'break-all',
          }}
        >
          {tracking}
        </div>
      </div>

      {/* Bottom: full-width Code128 barcode */}
      <div style={{ lineHeight: 0 }}>
        <Barcode
          value={tracking}
          format="CODE128"
          height={80}
          width={2}
          fontSize={0}
          displayValue={false}
          margin={0}
          background="#ffffff"
          lineColor="#000000"
        />
      </div>
    </div>
  );
}
